import React, { Fragment } from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

interface AccordionSectionProps {
  title: string;
  description?: string;
  defaultOpen?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
}

export const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  description,
  defaultOpen = false,
  icon: Icon,
  children,
}) => {
  return (
    <Disclosure defaultOpen={defaultOpen} as="div">
      {({ open }) => (
        <div
          className={`rounded-xl bg-white shadow-sm transition-colors ${
            open
              ? 'border-2 border-blue-200 bg-blue-50/40 shadow-md'
              : 'border border-gray-200/80 shadow-sm'
          }`}
        >
          <Disclosure.Button
            className={`flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset rounded-xl ${
              open ? 'hover:bg-blue-50/60' : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex min-w-0 items-start gap-3">
              {Icon ? (
                <span
                  className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                    open ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
              ) : null}
              <div className="min-w-0">
                <span className="block text-sm font-bold text-slate-900">{title}</span>
                {description ? (
                  <span className="mt-0.5 block text-[11px] font-normal leading-snug text-slate-500">
                    {description}
                  </span>
                ) : null}
              </div>
            </div>
            <ChevronDownIcon
              className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180 text-blue-500' : ''}`}
              aria-hidden="true"
            />
          </Disclosure.Button>
          <Transition
            as={Fragment}
            enter="transition duration-100 ease-out"
            enterFrom="transform opacity-0 -translate-y-1"
            enterTo="transform opacity-100 translate-y-0"
            leave="transition duration-75 ease-out"
            leaveFrom="transform opacity-100 translate-y-0"
            leaveTo="transform opacity-0 -translate-y-1"
          >
            <Disclosure.Panel className="border-t border-blue-100/80 px-4 py-4 space-y-4">
              {children}
            </Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  );
};
